import axios from "axios";
import fire from "../config/Fire";
import cogoToast from "@successtar/cogo-toast";

// Request interceptor: attach Firebase ID token to every request
axios.interceptors.request.use(
  async (config) => {
    const user = fire.auth().currentUser;
    if (user) {
      const token = await user.getIdToken(/* forceRefresh */ false);
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// 403 (authorization) handling — centralized, non-destructive.
//
// The hardened backend (branch security-authz-2026-07) returns
// `403 { error: "FORBIDDEN" }` when an agent touches a record outside their own
// book (out-of-scope get-by-id, update, or create-attach). A 403 is an
// AUTHORIZATION result, NOT a login problem, so it must NEVER trigger the
// 401 -> /login redirect below. We surface one calm, debounced "no access"
// toast and let the caller's own .catch (if any) still run.
//
// Many callers in this codebase have no .catch (e.g. ClientDatabase,
// PolicyDatabase, QuoteDatabase each have zero), so a rejected 403 would
// otherwise become an unhandled promise rejection. We tag the error
// (`error.isForbidden = true`) and install a global `unhandledrejection` guard
// that silently swallows ONLY those tagged 403s — every other rejection still
// propagates normally. Net effect: no white screen, no noisy uncaught error,
// and one consistent user-facing message everywhere. This handler is
// backward-compatible: against the old backend (which never sends 403s) it is
// simply never exercised, so the frontend can ship ahead of the backend deploy.
// ---------------------------------------------------------------------------
let lastForbiddenNoticeAt = 0;

function notifyForbidden() {
  // Debounce so a burst of out-of-scope requests can't spawn a toast storm.
  const now = Date.now();
  if (now - lastForbiddenNoticeAt < 4000) {
    return;
  }
  lastForbiddenNoticeAt = now;
  try {
    cogoToast.warn(
      "You don't have access to that record — it may not be in your book. " +
        "If you believe this is an error, contact an administrator.",
      { position: "top-center", hideAfter: 6 }
    );
  } catch (toastError) {
    // Never let user-feedback code break the app.
    console.warn("Forbidden notice failed to render:", toastError);
  }
}

// 429 (rate limit): the deployed limiter is 100 req/15min/IP and an ordinary
// session spends several calls per page view, so a busy moment is expected.
// Surface it calmly and NON-destructively — never a logout, never a scary
// error — and honor the fact that it's transient.
let lastBusyNoticeAt = 0;

function notifyBusy() {
  const now = Date.now();
  if (now - lastBusyNoticeAt < 8000) {
    return;
  }
  lastBusyNoticeAt = now;
  try {
    cogoToast.info("The system's a little busy — give it a moment and try again.", {
      position: "top-center",
      hideAfter: 5
    });
  } catch (toastError) {
    console.warn("Busy notice failed to render:", toastError);
  }
}

if (typeof window !== "undefined") {
  // Safety net for the many fetches in this codebase that lack a .catch: swallow
  // ONLY our tagged 403/429 rejections so they don't surface as uncaught errors.
  // Anything else is left untouched so real bugs still show up in the console.
  window.addEventListener("unhandledrejection", (event) => {
    if (event && event.reason && (event.reason.isForbidden || event.reason.isRateLimited)) {
      event.preventDefault();
    }
  });
}

// Response interceptor:
//   403 -> non-destructive "no access" notice (authorization; do NOT redirect)
//   401 -> token refresh + retry once, else redirect to /login (authentication)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response && error.response.status;

    // 403 FORBIDDEN: authorization result, not a login problem. Do NOT redirect.
    // Requests sent with { silentForbidden: true } (the AuthProvider role
    // probes, where a 403 just means "not this role") skip the user-facing
    // toast but are still tagged and rejected like any other 403.
    if (status === 403) {
      error.isForbidden = true;
      if (!(error.config && error.config.silentForbidden)) {
        notifyForbidden();
      }
      return Promise.reject(error);
    }

    // 429 TOO MANY REQUESTS: a transient rate limit, NOT an auth failure — do
    // NOT redirect to /login. Tag it (quiets unhandled rejections) and show a
    // calm debounced notice; the role probes ({ silentForbidden }) skip the
    // toast because AuthProvider surfaces "busy" on the login page + auto-retries.
    if (status === 429) {
      error.isRateLimited = true;
      if (!(error.config && error.config.silentForbidden)) {
        notifyBusy();
      }
      return Promise.reject(error);
    }

    if (status === 401) {
      const originalRequest = error.config;
      const errorCode = error.response.data && error.response.data.error;

      // If token expired and we haven't already retried, refresh and retry once
      if (errorCode === "TOKEN_EXPIRED" && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const user = fire.auth().currentUser;
        if (user) {
          try {
            const newToken = await user.getIdToken(/* forceRefresh */ true);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
          }
        }
      }

      // If not a token refresh case, or retry failed, redirect to login.
      // Carry the current location so the rebuilt login page can return the
      // user to where they were (and explain that they were signed out).
      if (window.location.pathname !== "/login" && window.location.pathname !== "/signup") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = "/login?next=" + next;
      }
    }
    return Promise.reject(error);
  }
);

export default {
  // Gets all books
  testAPI: function () {
    console.log("Running Test API");
    return axios.get("/api/");
  },

  //~~ Role probes (AuthProvider session restore) — same endpoints as the
  //   find*Login calls below, but a 403 here means "not this role", so the
  //   global forbidden toast is suppressed via silentForbidden.
  probeClientLogin: function (id) {
    return axios.get("/api/clients/login/" + id, { silentForbidden: true });
  },
  probeAgentLogin: function (id) {
    return axios.get("/api/agents/login/" + id, { silentForbidden: true });
  },
  probeAdminLogin: function (id) {
    return axios.get("/api/admins/login/" + id, { silentForbidden: true });
  },

  //~~ PROVISIONAL (Session H contract in flight — ROLE-DESIGN Part 5/9).
  //   The Activity read path below is a placeholder pending the Coordinator's
  //   answer; it is only exercised behind FEATURES.activityFeed (default OFF)
  //   against the local dev stub. Swap the path when the real endpoint ships.
  getActivitiesForClient: function (id) {
    return axios.get("/api/activities/client/" + id);
  },
  // Pre-agreed contract (Coordinator ruling 2026-07-11, Session H building):
  // bare array of document metadata for an owner, newest first; referral tier
  // sees own uploads only. Exercised behind FEATURES.documentsByOwner until
  // the endpoint deploys.
  getDocumentsByOwner: function (ownerModel, ownerRef) {
    return axios.get("/api/documents/owner/" + ownerModel + "/" + ownerRef);
  },

  // Ratified contract (Coordinator 2026-07-11): needsReview queue,
  // writing/admin only. Same flag family as the activity feed.
  getActivitiesReview: function () {
    return axios.get("/api/activities/review");
  },
  // Ratified contract (Coordinator 2026-07-11): duplicate-check before a
  // referral submit. Returns ONLY {match: "none"|"own"|"other"} plus minimal
  // own-book display fields — the "other" variant must stay nameless.
  checkProspectDuplicate: function (data) {
    return axios.post("/api/prospects/duplicate-check", data);
  },
  getProspects: function () {
    return axios.get("/api/prospects");
  },
  //~~ Nudges — PINNED contract (Coordinator, 2026-07-11; H Tranche 4):
  //   bare array, referral self-scoped, prospectName denormalized;
  //   respond tolerates a same-decider overwrite ≤60s then 409.
  getNudges: function () {
    return axios.get("/api/nudges");
  },
  respondToNudge: function (id, data) {
    return axios.put("/api/nudges/" + id + "/respond", data);
  },
  // Ratified (H Tranche 2, relayed 2026-07-11): PATCH, single updated
  // prospect back; 403 STAGE_LOCKED on terminal for non-admin; lost without
  // a reason → 400 LOST_REASON_REQUIRED (the UI always collects one).
  setProspectStage: function (id, data) {
    return axios.patch("/api/prospects/" + id + "/stage", data);
  },
  // PINNED Phase-3 (Coordinator/Session-I 2026-07-12): link a prospect to an
  // EXISTING client — the migrated-prospect sync. Writing tier only; advances
  // the stage to bound (meta.linkedExisting) and clears lostReason on un-lost.
  // Returns the updated prospect.
  linkProspectToClient: function (id, clientId) {
    return axios.post("/api/prospects/" + id + "/link-client", { clientId: clientId });
  },
  // Ratified (H Tranche 3): metadata attach after the browser uploads the
  // binary to Firebase Storage (the ruled v1 path). Owner + uploadedBy are
  // server-derived; anything body-forged is discarded.
  createDocumentForProspect: function (id, data) {
    return axios.post("/api/documents/prospect/" + id, data);
  },
  // Ratified (H Tranche 4): the staff reach-out queue (writing tier) —
  // entries carry the populated prospect + asking agent.
  getReachOuts: function () {
    return axios.get("/api/nudges/reach-outs");
  },
  // PINNED Phase-3 (Coordinator/Session-I 2026-07-12): close out a reach-out.
  // Writing tier; no body; precondition nudge.status === "reach_out"; sets
  // contactedAt/contactedBy (status stays reach_out); 409 if already contacted
  // (the UI treats that as success — the outcome is the same).
  markNudgeContacted: function (id) {
    return axios.patch("/api/nudges/" + id + "/contacted");
  },
  // Ratified (H Tranche 3): SR lifecycle. closed syncs legacy `completed`.
  claimServiceRequest: function (id) {
    return axios.patch("/api/servicerequests/" + id + "/claim");
  },
  assignServiceRequest: function (id, data) {
    return axios.patch("/api/servicerequests/" + id + "/assign", data);
  },
  setServiceRequestStatus: function (id, data) {
    return axios.patch("/api/servicerequests/" + id + "/status", data);
  },

  updateClientContact: function (data) {
    return axios.put("/api/clients/", data);
  },
  createLicense: function (data) {
    return axios.post("/api/licenses", data);
  },
  findLicense: function (data) {
    return axios.put("/api/licenses", data);
  },
  saveCarrier: function (data) {
    return axios.post("/api/carriers", data);
  },
  getCarriers: function () {
    return axios.get("/api/carriers");
  },
  updateCarrier: function (id, data) {
    return axios.put("/api/carriers/" + id, data);
  },

  //~~ Admin Routes
  getAdmins: function () {
    return axios.get("/api/admins");
  },
  findAdminLogin: function (id) {
    console.log("Running find admin login...");
    return axios.get("/api/admins/login/" + id);
  },
  saveAdmin: function (data) {
    return axios.post("/api/admins/", data);
  },

  //~~ Agent Routes
  updateAgent: function (id, data) {
    return axios.put("/api/agents/update/" + id, data);
  },
  getAgentAutos: function (id) {
    return axios.get("/api/agents/autos/" + id);
  },
  findAgent: function (id) {
    return axios.get("/api/agents/find/" + id);
  },
  getAgents: function () {
    return axios.get("/api/agents");
  },
  findAgentLogin: function (id) {
    return axios.get("/api/agents/login/" + id);
  },
  addClientToAgent: function (data) {
    return axios.put("/api/agents/clients", data);
  },
  getAgentProperties: function (id) {
    return axios.get("/api/agents/properties/" + id);
  },
  getAgentUmbrellas: function (id) {
    return axios.get("/api/agents/umbrellas/" + id);
  },
  getAgentClients: function (id) {
    return axios.get("/api/agents/clients/" + id);
  },
  getWritingAgentQuotes: function (id) {
    return axios.get("/api/quotes/" + id);
  },
  getAllAgentQuotes: function (id) {
    return axios.get("/api/agents/quotes/" + id);
  },
  saveAgent: function (data) {
    return axios.post("/api/agents", data);
  },
  saveAgentProspectSubmission: function (id, data) {
    console.log("Running saveAgentProspectSubmission API");
    console.log(data);
    return axios.post("/api/prospects/agent/" + id, data);
  },

  //~~ Application APIs
  createApplication: function (data) {
    return axios.post("/api/applications/", data);
  },
  createApplicationFromQuote: function (id, data) {
    return axios.post("/apli/applications/quote/" + id, data);
  },
  getApplications: function (data) {
    return axios.get("/api/applications");
  },
  getApplicationsByID: function (id) {
    return axios.get("/api/applications/" + id);
  },
  getApplicationByQuoteID: function (id) {
    return axios.get("/api/applications/quote/" + id);
  },
  updateApplicationByID: function (id, data) {
    return axios.put("/api/applications/" + id, data);
  },
  updateApplicationByQuoteID: function (id, data) {
    return axios.put("/api/applications/quote/" + id, data);
  },

  //~~ Auto APIs
  getAutos: function () {
    return axios.get("/api/autos");
  },
  getAutosFromArray: function (data) {
    return axios.post("/api/autos/bunchids", data);
  },
  findClientsAutoPolicies: function (id) {
    return axios.get("/api/autos/client/" + id);
  },
  saveAutoPolicy: function (id, data) {
    return axios.post("/api/autos/client/" + id, data);
  },
  saveAutoStandalonePolicy: function (data) {
    return axios.post("/api/autos/", data);
  },
  updateAutoPolicy: function (id, data) {
    return axios.put("/api/autos/policy/" + id, data);
  },
  createDocumentForAuto: function (id, data) {
    return axios.post("/api/documents/auto/" + id, data);
  },
  softDeleteAuto: function (id) {
    return axios.put("/api/autos/delete/" + id);
  },
  // PINNED Phase-3 (Session-I T4, 2026-07-12): inactivate/reactivate. The
  // "delete" path is the legacy softDelete (sets policyActive:false) — the UI
  // labels it "Mark inactive". reactivate flips it back. Both writing/admin
  // only, no body, return the UPDATED policy ({new:true}); a policy_status
  // Activity is emitted server-side.
  reactivateAuto: function (id) {
    return axios.put("/api/autos/reactivate/" + id);
  },

  //~~ Client APIs
  getClients: function () {
    return axios.get("/api/clients");
  },
  saveClient: function (data) {
    return axios.post("/api/clients", data);
  },
  convertClient: function (id, data) {
    return axios.post("/api/clients/convert/" + id, data);
  },
  findClient: function (id) {
    return axios.get("/api/clients/find/" + id);
  },
  findClientLogin: function (id) {
    return axios.get("/api/clients/login/" + id);
  },
  updateClient: function (id, data) {
    return axios.put("/api/clients/update/" + id, data);
  },

  //~~ Contacts APIs
  getContacts: function () {
    return axios.get("/api/contacts");
  },

  //~~ Carrier APIs

  //~~ Document APIs
  createDocument: function (data) {
    return axios.post("/api/documents", data);
  },
  createDocumentForProperty: function (id, data) {
    return axios.post("/api/documents/property/" + id, data);
  },
  createDocumentForQuote: function (id, data) {
    return axios.post("/api/documents/quote/" + id, data);
  },
  createDocumentsForQuoteProposal: function (id, data) {
    return axios.post("/api/documents/quote/proposal/" + id, data);
  },
  createDocumentForQuote1: function (id, data) {
    return axios.post("/api/documents/quote/1/" + id, data);
  },
  createDocumentForQuote2: function (id, data) {
    return axios.post("/api/documents/quote/2/" + id, data);
  },
  createDocumentForQuote3: function (id, data) {
    return axios.post("/api/documents/quote/3/" + id, data);
  },
  createDocumentForReferral: function (id, data) {
    return axios.post("/api/documents/referral/" + id, data);
  },
  createDocumentForServiceRequest: function (id, data) {
    return axios.post("/api/documents/servicerequest/" + id, data);
  },
  createDocumentForUmbrella: function (id, data) {
    return axios.post("/api/documents/umbrella/" + id, data);
  },
  createDocumentForVehicle: function (id, data) {
    return axios.post("/api/documents/vehicle/" + id, data);
  },
  createDocumentForClient: function (id, data) {
    return axios.post("/api/documents/client/" + id, data);
  },
  createDocumentForContact: function (id, data) {
    return axios.post("/api/documents/contact/" + id, data);
  },
  createDocumentForDriver: function (id, data) {
    return axios.post("/api/documents/driver/" + id, data);
  },
  createDocumentForEnrollment: function (id, data) {
    return axios.post("/api/documents/enrollment/" + id, data);
  },
  createDocumentForLicense: function (id, data) {
    return axios.post("/api/documents/license/" + id, data);
  },
  createDocumentForAgent: function (id, data) {
    return axios.post("/api/documents/agent/" + id, data);
  },
  createDocumentForApplication: function (id, data) {
    return axios.post("/api/documents/application/" + id, data);
  },
  getDocumentById: function (id) {
    return axios.get("/api/documents/" + id);
  },
  getDocumentsByIdArray: function (data) {
    return axios.post("/api/documents/group", data);
  },

  //~~ Driver APIs

  getDrivers: function () {
    return axios.get("/api/drivers");
  },
  saveDriver: function (id, data) {
    return axios.post("/api/drivers/" + id, data);
  },
  findDrivers: function (id) {
    return axios.get("/api/drivers/" + id);
  },
  softDeleteDriver: function (id) {
    return axios.put("/api/drivers/" + id);
  },

  //~~ Enrollments API
  saveEnrollmentData: function (data) {
    return axios.post("/api/enrollments", data);
  },

  //~~ Orders API

  getOrder: function () {
    return axios.get("/api/orders");
  },
  saveOrder: function (data) {
    return axios.post("/api/orders");
  },
  saveOrderForExistingClient: function (id, data) {
    return axios.post("/api/orders/client/" + id, data);
  },
  saveOrderForReferringAgent: function (id, data) {
    return axios.post("/api/orders/referringagent/" + id, data);
  },
  assignOrderToServicer: function (id, data) {
    return axios.post("/api/orders/servicingagent/" + id, data);
  },
  assignOrderToWritingAgent: function (id, data) {
    return axios.post("/api/orders/writingagent/" + id, data);
  },

  //~~ Properties APIs

  getProperties: function () {
    return axios.get("/api/properties");
  },
  getPropertiesFromArray: function (data) {
    return axios.post("/api/properties/bunchids", data);
  },
  savePropertyPolicy: function (id, data) {
    return axios.post("/api/properties/client/" + id, data);
  },
  savePropertyStandalonePolicy: function (data) {
    return axios.post("/api/properties/", data);
  },
  updatePropertyPolicy: function (id, data) {
    return axios.put("/api/properties/policy/" + id, data);
  },
  softDeleteProperty: function (id) {
    return axios.put("/api/properties/delete/" + id);
  },
  reactivateProperty: function (id) {
    return axios.put("/api/properties/reactivate/" + id);
  },

  //~~ Quote APIs

  editQuote: function (id, data) {
    return axios.put("/api/quotes/edit/" + id, data);
  },
  getQuotesFromArray: function (data) {
    return axios.post("/api/quotes/bunchids", data);
  },
  getQuotes: function () {
    return axios.get("/api/quotes");
  },
  getQuotesForAgent: function (id) {
    return axios.get("/api/quotes/agent/" + id);
  },
  getQuote: function (id) {
    return axios.get("/api/quotes/loc/" + id);
  },
  saveQuote: function (data) {
    return axios.post("/api/quotes", data);
  },
  // Public landing-page lead form — unauthenticated endpoint (no Bearer token). The authenticated
  // saveQuote above posts to the token-gated /api/quotes; anonymous visitors must use this route.
  // Backend: quotesController.createPublicQuote (strict whitelist), mounted before the /api guard.
  savePublicQuote: function (data) {
    return axios.post("/api/public/quote", data);
  },
  updateQuote: function (id, data) {
    return axios.put("/api/quotes/update/" + id, data);
  },
  saveAgentQuoteRequest: function (id, data) {
    return axios.post("/api/quotes/" + id, data);
  },

  //~~ Referrals APIs

  getReferrals: function () {
    return axios.get("/api/referrals");
  },
  saveReferral: function (data) {
    return axios.post("/api/referrals", data);
  },

  //~~ Servicing APIs

  updateServiceRequest: function (id, data) {
    return axios.put("/api/servicerequests/" + id, data);
  },
  getServicers: function () {
    return axios.get("/api/servicers");
  },
  getServicerequests: function () {
    return axios.get("/api/servicerequests");
  },
  getServiceRequestsForManyClients: function (data) {
    return axios.post("/api/servicerequests/agent", data);
  },
  saveServiceRequest: function (data) {
    return axios.post("/api/servicerequests", data);
  },
  saveServiceRequestForClient: function (data, id) {
    return axios.post("/api/servicerequests/client/" + id, data);
  },

  //~~ Umbrella APIs

  getUmbrellas: function () {
    return axios.get("/api/umbrellas");
  },
  getUmbrellasFromArray: function (data) {
    return axios.post("/api/umbrellas/bunchids", data);
  },
  saveUmbrellaPolicy: function (id, data) {
    return axios.post("/api/umbrellas/client/" + id, data);
  },
  saveUmbrellaStandalonePolicy: function (data) {
    return axios.post("/api/umbrellas/", data);
  },
  updateUmbrellaPolicy: function (id, data) {
    return axios.put("/api/umbrellas/policy/" + id, data);
  },
  softDeleteUmbrella: function (id) {
    return axios.put("/api/umbrellas/delete/" + id);
  },
  reactivateUmbrella: function (id) {
    return axios.put("/api/umbrellas/reactivate/" + id);
  },

  //~~ Vehicles APIs

  getVehicles: function () {
    return axios.get("/api/vehicles");
  },
  findVehicles: function (id) {
    return axios.get("/api/vehicles/" + id);
  },
  saveVehicle: function (id, data) {
    return axios.post("/api/vehicles/" + id, data);
  },
  softDeleteVehicle: function (id) {
    return axios.put("/api/vehicles/" + id);
  },

  //~~ Custodian Data-Fix queue (§10.7b) — pinned T1. Findings are OPEN
  // custodian_finding Activities; resolve books a finding resolved/dismissed
  // AFTER the FE applies the fix through the existing status/link/edit affordances.
  getCustodianQueue: function (params) {
    return axios.get("/api/custodian/queue", { params });
  },
  getCustodianQueueCount: function () {
    return axios.get("/api/custodian/queue/count");
  },
  resolveCustodianFinding: function (id, body) {
    return axios.patch("/api/custodian/findings/" + id + "/resolve", body);
  },

  //~~ Questions (§10.7c/§10.7d) — pinned T3. POST is shared by "Ask the Fawn"
  // (ambiguous findings) and the Fawn bar; over-280 is TRIMMED server-side, not
  // rejected (response carries {truncated, cap}). GET is the receipts + staff
  // apply feed; resolve is the A6 plain-text path.
  createQuestion: function (body) {
    return axios.post("/api/questions", body);
  },
  getQuestions: function (params) {
    return axios.get("/api/questions", { params });
  },
  resolveQuestion: function (id, body) {
    return axios.patch("/api/questions/" + id + "/resolve", body);
  }
};
